/**
 * 設備：AC-H-3 儀表板、設備資產 CRUD，以及 D-2 離線檢票的裝置隔離 /
 * 強制收回（PRD §D-2 / AC-D2-13/14/2a8）。
 */

import { apiFetch } from './core';

// ---------- AC-H-3：設備儀表板（admin.device_view） ----------

export interface DeviceKpis {
  total: number;
  online: number;
  offline_over_5min: number;
  errors_24h_total: number;
}

export interface DeviceListItem {
  id: string;
  device_code: string;
  device_type: string;
  location: string | null;
  online: boolean;
  last_heartbeat: string | null;
  current_version: string | null;
  staging_version: string | null;
  battery_level: number | null;
  printer_paper_pct: number | null;
  errors_24h: number;
  retired_at: string | null;
}

export interface DeviceErrorBucket {
  code: string;
  count: number;
}

export interface DeviceDashboardResponse {
  kpis: DeviceKpis;
  devices: DeviceListItem[];
  error_distribution_24h: DeviceErrorBucket[];
}

/**
 * GET /admin/devices — 設備儀表板（AC-H-3）。
 *
 * 回 { kpis, devices[], error_distribution_24h[] }。需 admin.device_view。
 */
export function getDeviceDashboard(): Promise<DeviceDashboardResponse> {
  return apiFetch<DeviceDashboardResponse>('/admin/devices');
}

/** 設備資產列（登錄 / 編輯 / 退役回傳）。 */
export interface DeviceRow {
  id: string;
  device_code: string;
  device_type: string;
  location: string | null;
  registered_at: string | null;
  retired_at: string | null;
}

/** 可登錄之設備類型（與後端 DEVICE_TYPES 對齊）。 */
export const DEVICE_TYPES = ['kiosk', 'printer', 'scanner', 'turnstile', 'tablet'] as const;

/**
 * POST /admin/devices — 登錄新設備（device provisioning）。需 admin.device_manage。
 * device_code 唯一；衝突 → 409 DEVICE_CODE_TAKEN。
 */
export function createDevice(input: {
  device_code: string;
  device_type: string;
  location?: string;
}): Promise<DeviceRow> {
  return apiFetch<DeviceRow>('/admin/devices', { method: 'POST', body: input });
}

/**
 * PATCH /admin/devices/:id — 編輯 / 退役（retired=true）/ 復役（retired=false）。
 * 需 admin.device_manage。
 */
export function updateDevice(
  id: string,
  patch: { location?: string; device_type?: string; retired?: boolean },
): Promise<DeviceRow> {
  return apiFetch<DeviceRow>(`/admin/devices/${id}`, { method: 'PATCH', body: patch });
}

// ---------- D-2 離線檢票（裝置隔離 + 強制收回）----------

/**
 * Device quarantine row — 對齊 BE device-quarantine.controller.ts 之 QuarantineRowWire。
 * timestamptz 皆為 ISO-8601 字串。released_at 非 NULL = 已釋放。
 */
export interface QuarantineRow {
  id: string;
  device_id: string;
  trip_id: string | null;
  reason: string;
  quarantined_at: string;
  released_at: string | null;
  released_by_user_id: string | null;
  created_at: string;
}

/** GET /admin/device-quarantines/active — 列出所有 active（released_at IS NULL）。 */
export function listActiveQuarantines(): Promise<{ rows: QuarantineRow[] }> {
  return apiFetch<{ rows: QuarantineRow[] }>('/admin/device-quarantines/active', {
    method: 'GET',
  });
}

/**
 * POST /admin/device-quarantines/:device_id/release — 釋放 device quarantine（限 SuperAdmin）。
 * released_by_user_id 須為當前登入 superadmin 之 user_id（取自 getMe()）。
 */
export function releaseQuarantine(
  deviceId: string,
  releasedByUserId: string,
): Promise<{ released_count: number; rows: QuarantineRow[] }> {
  return apiFetch<{ released_count: number; rows: QuarantineRow[] }>(
    `/admin/device-quarantines/${encodeURIComponent(deviceId)}/release`,
    { method: 'POST', body: { released_by_user_id: releasedByUserId } },
  );
}

/**
 * 強制收回結果（AC-D2-14）；對齊 BE forced-reclaim.service.ts 之 ReclaimResult。
 * reclaim_available_at = 替代裝置可取得新 lease 之時點（UI 倒數終點）。
 */
export interface ForcedReclaimResult {
  lease_id: string;
  trip_id: string;
  device_id: string;
  reason: string;
  quarantine_started_at: string;
  quarantine_window_seconds: number;
  reclaim_available_at: string;
}

/** forced_reclaim_log row（AC-D2-14 稽核軌跡）。 */
export interface ForcedReclaimLogEntry {
  id: string;
  trip_id: string;
  prev_lease_id: string | null;
  new_lease_id: string | null;
  reason: string;
  actor_user_id: string | null;
  quarantine_started_at: string | null;
  quarantine_window_seconds: number | null;
  occurred_at: string;
}

/**
 * POST /admin/leases/:lease_id/forced-reclaim — 強制收回 lease（AC-D2-14）。
 * reason 必填；actor_user_id 為當前操作者 user_id（取自 getMe()）。
 */
export function forcedReclaimLease(
  leaseId: string,
  payload: { reason: string; actor_user_id: string },
): Promise<ForcedReclaimResult> {
  return apiFetch<ForcedReclaimResult>(
    `/admin/leases/${encodeURIComponent(leaseId)}/forced-reclaim`,
    { method: 'POST', body: payload },
  );
}

/** GET /admin/leases/:lease_id/forced-reclaim-log — 查強制收回稽核紀錄。 */
export function getForcedReclaimLog(leaseId: string): Promise<{ rows: ForcedReclaimLogEntry[] }> {
  return apiFetch<{ rows: ForcedReclaimLogEntry[] }>(
    `/admin/leases/${encodeURIComponent(leaseId)}/forced-reclaim-log`,
    { method: 'GET' },
  );
}
